/**
 * Domain-tag filter - thin wrapper around `TagChipPicker` that
 * supplies the domain-color leading dot (so chip swatches match the
 * graph nodes) plus the section icon/label/clear wiring.
 *
 * Option list is the FIXED set of every domain tag across the user's
 * mindspace (via `useDomainTagOptions`) - independent of the currently
 * filtered graph. `TagChipPicker` owns the typeahead, scroll cap, and
 * pinned-selected sort behavior.
 */

import { useMemo, useState } from 'react';
import { Tag } from 'lucide-react';
import { useDomainTagOptions } from '@/hooks/mindspace/useMindspaceOptions';
import { useTagFilters } from '@/hooks/mindspace/useTagFilters';
import type { KnowledgeGraphNode } from '@/lib/api-client';
import { FilterSection } from '@/components/features/mindspace/filters/FilterSection';
import {
  TagChipPicker,
  toggleTagInList,
} from '@/components/features/mindspace/filters/TagChipPicker';

interface DomainTagFilterProps {
  /** Current filtered graph nodes - used only to compute the dim-when-zero hint. */
  nodes: KnowledgeGraphNode[] | undefined;
  /** Section open on first render. Story-only hatch, initial state only — no
   *  production call site passes it, and the panels all mount collapsed. It
   *  exists because the section body (chips, window pills, the lesson list) is
   *  otherwise reachable only by clicking, and a click-only state has no resting
   *  story to diff. Same escape hatch as `FlowSelector`/`SearchInput`'s
   *  `defaultOpen`. */
  defaultOpen?: boolean;
}

export function DomainTagFilter({
  nodes,
  defaultOpen = false,
}: DomainTagFilterProps) {
  const { selectedDomain, setFilters } = useTagFilters();
  const [open, setOpen] = useState(defaultOpen);

  const { data: options = [], isLoading } = useDomainTagOptions();

  const availableInView = useMemo(() => {
    const s = new Set<string>();
    for (const n of nodes ?? []) {
      for (const t of n.domain_tags ?? []) s.add(t);
    }
    return s;
  }, [nodes]);

  return (
    <FilterSection
      icon={<Tag className='h-3.5 w-3.5' />}
      label='Domain tags'
      count={selectedDomain.length}
      open={open}
      onOpenChange={setOpen}
      onClear={
        selectedDomain.length ? () => setFilters({ domain: [] }) : undefined
      }
    >
      <TagChipPicker
        options={options}
        selected={selectedDomain}
        onToggle={tag =>
          setFilters({ domain: toggleTagInList(selectedDomain, tag) })
        }
        availableInView={availableInView}
        dimUnavailable={false}
        isLoading={isLoading}
        emptyLabel='No domain tags yet'
        searchPlaceholder='Filter domains…'
      />
    </FilterSection>
  );
}
